import { NavLink } from "react-router-dom";
import {
  CreditCard,
  LayoutDashboard,
  LineChart,
  Settings,
  Users,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

interface NavSection {
  section: string;
  items: NavItem[];
}

const NAV: NavSection[] = [
  {
    section: "Overview",
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    section: "Finance",
    items: [
      { to: "/accounts", label: "Accounts", icon: Wallet },
      { to: "/credit-cards", label: "Credit cards", icon: CreditCard },
      { to: "/investments", label: "Investments", icon: LineChart },
    ],
  },
  {
    section: "Management",
    items: [
      { to: "/settings/users", label: "Users", icon: Users, adminOnly: true },
    ],
  },
  {
    section: "Settings",
    items: [
      { to: "/settings", label: "Settings", icon: Settings, adminOnly: true },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onNavigate?: () => void;
}

export function Sidebar({ collapsed, onNavigate }: SidebarProps) {
  const { user } = useAuth();
  const sections = NAV.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => !item.adminOnly || user?.role === "admin",
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <nav
      aria-label="Primary"
      className={cn(
        "flex h-full flex-col border-r border-border bg-background transition-[width] duration-200",
        collapsed ? "w-[64px] px-2" : "w-[240px] px-3",
        "py-4",
      )}
    >
      <div
        className={cn(
          "mb-4 flex items-center gap-2 px-1",
          collapsed && "justify-center px-0",
        )}
      >
        <div className="grid h-7 w-7 place-items-center rounded-md bg-primary text-primary-foreground">
          <span className="font-mono text-xs font-semibold">F</span>
        </div>
        {!collapsed ? (
          <span className="text-sm font-semibold tracking-tight">Finance</span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-4">
        {sections.map((section, sectionIndex) => (
          <div key={section.section} className="flex flex-col gap-1">
            {!collapsed ? (
              <p
                className="px-2 pt-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
                id={`nav-section-${section.section.toLowerCase()}`}
              >
                {section.section}
              </p>
            ) : sectionIndex > 0 ? (
              <div
                aria-hidden
                className="mx-2 my-1 h-px bg-border"
              />
            ) : null}
            <ul
              className="flex flex-col gap-0.5"
              aria-labelledby={
                !collapsed
                  ? `nav-section-${section.section.toLowerCase()}`
                  : undefined
              }
              aria-label={collapsed ? section.section : undefined}
            >
              {section.items.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    // "end" prevents an ancestor route from staying active when a
                    // descendant is rendered. /settings needs it so that
                    // /settings/users doesn't also light up /settings.
                    end={to === "/" || to === "/settings"}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                        isActive && "bg-muted text-foreground",
                        collapsed && "justify-center px-0",
                      )
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    {!collapsed ? (
                      <span className="truncate">{label}</span>
                    ) : null}
                    {collapsed ? (
                      <span className="sr-only">{label}</span>
                    ) : null}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {!collapsed ? (
        <div className="mt-4 rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">Workspace</p>
          <p className="font-mono">lastcow/finance</p>
        </div>
      ) : null}
    </nav>
  );
}
